// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import {
  Directive, ElementRef, HostListener, Input, OnDestroy, ViewContainerRef
} from '@angular/core';
import {
  Overlay, OverlayRef, FlexibleConnectedPositionStrategy, ConnectedPosition
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { WithAnnotTooltipCardComponent } from './annot-tooltip-card.component';

type TooltipPosition = 'right' | 'left' | 'above' | 'below';

@Directive({
  selector: '[withAnnotTooltip]',
  standalone: true,
})
export class WithAnnotTooltipDirective implements OnDestroy {
  @Input('withAnnotTooltip') text = '';
  @Input() withAnnotTooltipPosition: TooltipPosition = 'right';
  @Input() withAnnotTooltipDisabled = false;

  private overlayRef?: OverlayRef;

  constructor(
    private el: ElementRef<HTMLElement>,
    private overlay: Overlay,
    private vcr: ViewContainerRef,
  ) { }

  @HostListener('mouseenter')
  onEnter() {
    if (this.withAnnotTooltipDisabled || !this.text) return;
    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        positionStrategy: this.buildPositionStrategy(),
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        hasBackdrop: false,
        panelClass: 'with-annot-tooltip-panel',
      });
    }
    const portal = new ComponentPortal(WithAnnotTooltipCardComponent, this.vcr);
    const ref = this.overlayRef.attach(portal);
    ref.instance.text = this.text;
  }

  @HostListener('mouseleave')
  onLeave() {
    this.detach();
  }

  ngOnDestroy(): void {
    this.detach(true);
  }

  private detach(dispose = false) {
    if (!this.overlayRef) return;
    this.overlayRef.detach();
    if (dispose) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
  }

  private buildPositionStrategy(): FlexibleConnectedPositionStrategy {
    const origin = this.overlay.position().flexibleConnectedTo(this.el);
    const positions: Record<TooltipPosition, ConnectedPosition[]> = {
      right: [
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
      ],
      left: [
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
      ],
      above: [
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
      ],
      below: [
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
      ],
    };

    return origin
      .withPositions(positions[this.withAnnotTooltipPosition])
      .withPush(true)
      .withViewportMargin(8);
  }
}
